import { formatLongDate, formatDateTime } from '@/lib/date-time';
/**
 * @file components/workbench/DeciderWorkbench.tsx
 * @description Decider（驗證委員）角色工作台（完整版，使用設計 tokens + shell 組件）。
 *
 * 信息架構（從上到下）：
 *   1. PageShell header — eyebrow + 大標問候 + meta
 *   2. 4 張 StatCard — 待投票 / 本月通過 / 延議中 / 委員會出席率
 *   3. 待投票決定（主要列表）+ 近期決定結果（右欄）
 *   4. 本月投票統計 + 近期動態
 *
 * 資料來源：useDeciderDashboard() → GET /api/dashboard/decider → DeciderDashboardData
 */

import { Button, Empty, Table, Tag } from 'antd';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/lib/auth-context';
import { useDeciderDashboard } from '@/hooks/useWorkbench';
import { PageShell, PageMetaItem, SectionCard, StatCard, StatusPill } from '@/components/shell';
import { color, font, leading, radius, space, text, weight } from '@/styles/tokens';
import type { DeciderDashboardData } from '@zhcb/shared';
import {
  Activity,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock,
  Gavel,
  Scale,
  Vote,
  XCircle,
} from 'lucide-react';

// ─── 類型別名 ──────────────────────────────────────────────────────────────────

/** 待投票決定列表項型別（從 DTO 衍生） */
type PendingVoteItem = DeciderDashboardData['pendingVote'][number];

/** 近期決定結果列表項型別（從 DTO 衍生） */
type RecentDecisionItem = DeciderDashboardData['recentDecisions'][number];

/** 近期動態列表項型別（從 DTO 衍生） */
type ActivityItem = DeciderDashboardData['recentActivity'][number];

/** 投票統計項型別 */
type VoteStatItem = DeciderDashboardData['voteStats'][number];

// ─── 輔助函式 ──────────────────────────────────────────────────────────────────

/** 根據決定結果字串映射顯示色調 */
function resultToTone(result: string): 'success' | 'warning' | 'danger' {
  if (result === '通過') return 'success';
  if (result === '不通過') return 'danger';
  return 'warning';
}

/** 根據投票統計標籤映射進度條顏色 */
function getVoteColor(label: string): string {
  const map: Record<string, string> = {
    通過: color.state.success,
    不通過: color.state.danger,
    延議: color.state.warning,
  };
  return map[label] ?? color.text.muted;
}

// ─── 子組件 ────────────────────────────────────────────────────────────────────

/**
 * 投票統計條。
 * 以水平進度條顯示每種投票結果的佔比。
 */
function VoteStatsRow({ stats }: { stats: VoteStatItem[] }) {
  const total = stats.reduce((sum, s) => sum + s.count, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
      {stats.map((s) => {
        const pct = total > 0 ? (s.count / total) * 100 : 0;
        const barColor = getVoteColor(s.label);
        return (
          <div key={s.label}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: text.md,
                  color: color.text.secondary,
                  fontWeight: weight.medium,
                }}
              >
                {s.label}
              </span>
              <span
                style={{
                  fontFamily: font.display,
                  fontSize: text.md,
                  fontWeight: weight.semibold,
                  color: color.text.primary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {s.count}
                <span
                  style={{
                    fontSize: text.sm,
                    color: color.text.muted,
                    fontWeight: weight.regular,
                    marginLeft: 6,
                  }}
                >
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: color.surface.sunken,
                borderRadius: radius.full,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: barColor,
                  borderRadius: radius.full,
                  transition: 'width 480ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 近期決定結果列。
 * 顯示決定編號、結果標籤、客戶名稱和日期。
 */
function DecisionResultRow({ item }: { item: RecentDecisionItem }) {
  const tone = resultToTone(item.result);

  const iconMap = {
    success: <CheckCircle2 size={16} />,
    warning: <Clock size={16} />,
    danger: <XCircle size={16} />,
  };

  const bgMap = {
    success: color.state.successSoft,
    warning: color.state.warningSoft,
    danger: color.state.dangerSoft,
  };

  const fgMap = {
    success: color.state.success,
    warning: color.state.warning,
    danger: color.state.danger,
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: radius.md,
          background: bgMap[tone],
          color: fgMap[tone],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {iconMap[tone]}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginBottom: 3,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.brand.primary,
              fontWeight: weight.semibold,
            }}
          >
            {item.code}
          </span>
          <StatusPill tone={tone} size="sm">
            {item.result}
          </StatusPill>
        </div>
        <div
          style={{
            fontSize: text.md,
            fontWeight: weight.medium,
            color: color.text.primary,
            lineHeight: leading.snug,
          }}
        >
          {item.clientName ?? '—'}
        </div>
        <div style={{ fontSize: text.sm, color: color.text.muted, marginTop: 2 }}>
          {item.date ?? '—'}
        </div>
      </div>
    </div>
  );
}

/**
 * 近期動態列表項。
 * 顯示操作者、動作描述、對象和相對時間。
 */
function ActivityRow({ item, isLast }: { item: ActivityItem; isLast: boolean }) {
  return (
    <div
      style={{
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: isLast ? undefined : `1px solid ${color.border.subtle}`,
      }}
    >
      <div style={{ fontSize: text.md, color: color.text.primary, lineHeight: leading.snug }}>
        <span style={{ fontWeight: weight.semibold }}>{item.actor}</span>
        <span style={{ color: color.text.tertiary, margin: '0 4px' }}>{item.action}</span>
        <span style={{ fontWeight: weight.medium }}>{item.target}</span>
      </div>
      <div style={{ fontSize: text.sm, color: color.text.muted, marginTop: 2 }}>{item.time}</div>
    </div>
  );
}

// ─── 主組件 ────────────────────────────────────────────────────────────────────

/**
 * Decider 工作台組件。
 * 顯示待投票決定、近期決定結果、投票統計和近期動態。
 * 全部資料來自 useDeciderDashboard() (GET /api/dashboard/decider)。
 */
export function DeciderWorkbench() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);

  const hour = new Date().getHours();
  const greeting = hour < 6 ? '深夜好' : hour < 12 ? '早安' : hour < 18 ? '午安' : '晚安';

  /** 取得 Decider 工作台聚合資料 */
  const { data: d, isLoading } = useDeciderDashboard();

  const pendingDecisions = d?.pendingVote ?? [];
  const pendingCount = d?.pendingVoteTotal ?? 0;

  /** 委員會出席率：後端簡化版本；0 時顯示「—」 */
  const attendanceDisplay: string | number =
    d?.attendanceRate !== undefined && d.attendanceRate > 0 ? d.attendanceRate : '—';

  /** 本月投票統計描述 */
  const voteStatsTotal = (d?.voteStats ?? []).reduce((s, v) => s + v.count, 0);

  /** 待投票表格列定義 */
  const columns = [
    {
      title: '決定編號',
      dataIndex: 'code',
      width: 160,
      render: (code: string) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag color="orange">{s}</Tag>,
    },
    {
      title: '排程時間',
      dataIndex: 'scheduledAt',
      width: 130,
      render: (dateStr: string | null) =>
        dateStr
          ? formatDateTime(dateStr)
          : '—',
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_: unknown, record: PendingVoteItem) => (
        <Button
          type="link"
          size="small"
          onClick={() => void navigate({ to: '/decisions/$id', params: { id: String(record.id) } })}
        >
          投票
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title={
        <span>
          {greeting}，{user.name}
        </span>
      }
      description={
        <>
          目前有{' '}
          <strong style={{ color: pendingCount > 0 ? color.state.danger : color.text.primary }}>
            {pendingCount}
          </strong>{' '}
          個驗證決定等待您的投票。委員會是確保驗證公正性的最後防線。
        </>
      }
      meta={
        <>
          <PageMetaItem
            icon={<CalendarDays size={13} />}
            label="日期"
            value={formatLongDate(new Date())}
          />
          <PageMetaItem icon={<Vote size={13} />} label="待投票" value={`${pendingCount} 件`} />
        </>
      }
      actions={
        <>
          <Button
            type="primary"
            icon={<Gavel size={14} />}
            onClick={() => void navigate({ to: '/decisions' })}
          >
            決定列表
          </Button>
        </>
      }
    >
      {/* ═══ 第一排：4 張統計卡 ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <StatCard
          title="待投票"
          value={pendingCount}
          suffix="件"
          tone={pendingCount > 0 ? 'danger' : 'success'}
          icon={<Vote size={20} />}
          onClick={() => void navigate({ to: '/decisions' })}
        />
        <StatCard
          title="本月通過"
          value={d?.monthlyApproved ?? '—'}
          suffix={d?.monthlyApproved !== undefined ? '件' : undefined}
          tone="success"
          icon={<CheckCircle2 size={20} />}
        />
        <StatCard
          title="本月延議"
          value={d?.monthlyDeferred ?? '—'}
          suffix={d?.monthlyDeferred !== undefined ? '件' : undefined}
          tone="warning"
          icon={<Clock size={20} />}
        />
        <StatCard
          title="委員會出席率"
          value={attendanceDisplay}
          suffix={typeof attendanceDisplay === 'number' ? '%' : undefined}
          tone="info"
          icon={<Scale size={20} />}
        />
      </div>

      {/* ═══ 待投票決定（左）+ 近期結果（右） ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <SectionCard
          icon={<Vote size={14} />}
          // 序：主委/委員工作台統一為「我的待辦事項」（參考管理組）；內容為待審議/待投票決定
          title="我的待辦事項"
          description="需要您參與審議 / 投票的驗證決定"
          actions={
            <Button
              type="text"
              size="small"
              icon={<ArrowRight size={13} />}
              onClick={() => void navigate({ to: '/decisions' })}
            >
              查看全部
            </Button>
          }
          padless
        >
          <Table
            dataSource={pendingDecisions}
            rowKey="id"
            size="small"
            loading={isLoading}
            pagination={false}
            columns={columns}
            locale={{ emptyText: <Empty description="目前無待投票決定" /> }}
          />
        </SectionCard>

        <SectionCard
          icon={<Gavel size={14} />}
          title="近期決定結果"
          description="最近完成的驗證決定"
          padless
        >
          {(d?.recentDecisions ?? []).length === 0 ? (
            <div style={{ padding: space[6] }}>
              <Empty description="暫無近期決定" />
            </div>
          ) : (
            (d?.recentDecisions ?? []).map((item) => (
              <DecisionResultRow key={item.code} item={item} />
            ))
          )}
        </SectionCard>
      </div>

      {/* ═══ 本月投票統計 + 近期動態 ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: space[4],
        }}
      >
        <SectionCard
          icon={<Scale size={14} />}
          title="本月投票統計"
          description={`共 ${voteStatsTotal} 次投票`}
        >
          {(d?.voteStats ?? []).length === 0 ? (
            <Empty description="暫無投票統計" />
          ) : (
            <VoteStatsRow stats={d?.voteStats ?? []} />
          )}
        </SectionCard>

        <SectionCard
          icon={<Activity size={14} />}
          title="近期動態"
          description="委員會最新事項"
          padless
        >
          {(d?.recentActivity ?? []).length === 0 ? (
            <div style={{ padding: space[6] }}>
              <Empty description="暫無近期動態" />
            </div>
          ) : (
            (d?.recentActivity ?? []).map((item, idx, arr) => (
              <ActivityRow key={item.id} item={item} isLast={idx === arr.length - 1} />
            ))
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
